<template>
  <div>
    <b-modal
      ref="billGiftModal"
      hide-footer
      :title-html="modalTitle"
      @hidden="onModalHidden"
    >
      <v-form ref="form">
        <!-- Danh sách quà tặng đã cấu hình trong Bộ phụ kiện tặng kèm -->
        <p class="mb-2 font-italic text-muted">
          Danh sách quà tặng đã cấu hình cho sản phẩm này (Cấu hình tại
          <strong>Ngành hàng → Bộ phụ kiện tặng kèm</strong>). Tích chọn quà
          muốn thêm rồi bấm "Áp dụng". Quà đã có trong hóa đơn được đánh dấu "đã
          thêm".
        </p>
        <p
          v-if="!isLoading && !giftOptions.length"
          class="text-danger font-italic"
        >
          Chưa cấu hình quà tặng cho sản phẩm này.
        </p>

        <b-table
          :items="giftOptions"
          :fields="tableFields"
          :busy="isLoading"
          bordered
          hover
          small
          responsive
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner small></b-spinner>
            </div>
          </template>
          <template #head(selected)>
            <b-form-checkbox
              v-model="selectAll"
              @change="onToggleSelectAll"
            ></b-form-checkbox>
          </template>
          <template #cell(selected)="row">
            <b-form-checkbox
              v-model="row.item.selected"
              :disabled="row.item.alreadyAttached"
            ></b-form-checkbox>
          </template>
          <template #cell(name)="row">
            <span
              >{{ row.item.name }}
              <span
                v-if="row.item.alreadyAttached"
                class="text-muted font-italic"
                style="font-size: 11px"
                >(đã thêm)</span
              ></span
            >
          </template>
        </b-table>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-3 column-gap">
          <b-button
            variant="secondary"
            size="sm"
            @click="hideModal"
            class="me-2"
          >
            Hủy
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="applyGifts"
            :disabled="selectedCount === 0"
          >
            Áp dụng ({{ selectedCount }})
          </b-button>
        </div>
      </v-form>
    </b-modal>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { convertPrice, makeToastFaile, makeToastSuccess } from '@/utils/common';
import { BILL_ITEM_TYPE } from '@/utils/enum';
import ApiService from '@/core/services/api.service';

export default {
  name: 'BillGiftModal',

  props: {
    billItemId: {
      type: [String, null],
      required: true,
    },
    storeId: {
      type: [String, null],
      required: true,
    },
    productName: {
      type: [String, null],
      required: false,
    },
    // SP chính để load danh sách quà cấu hình (Bộ phụ kiện tặng kèm).
    productId: {
      type: [String, null],
      required: false,
      default: null,
    },
    // productId các quà ĐÃ gắn dưới dòng → disable, tránh thêm trùng.
    attachedGiftProductIds: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      modalTitle: '',
      giftOptions: [],
      isLoading: false,
      selectAll: false,
    };
  },

  computed: {
    selectedCount() {
      return this.giftOptions.filter((g) => g.selected).length;
    },

    tableFields() {
      return [
        {
          key: 'selected',
          label: '',
          sortable: false,
          thStyle: { ...this.headerStyle, width: '40px', textAlign: 'center' },
          tdClass: 'text-center',
        },
        {
          key: 'index',
          label: 'STT',
          sortable: false,
          thStyle: this.headerStyle,
          tdClass: 'text-center',
          formatter: (value, key, item) => {
            return this.giftOptions.findIndex((p) => p.id === item.id) + 1;
          },
        },
        {
          key: 'name',
          label: 'Quà tặng',
          sortable: false,
          thStyle: this.headerStyle,
        },
        {
          key: 'quantityInStock',
          label: 'Tồn kho',
          sortable: false,
          thStyle: this.headerStyle,
          tdClass: 'text-end',
          formatter: (value) => value || 0,
        },
        {
          key: 'price',
          label: 'Giá bán',
          sortable: false,
          thStyle: this.headerStyle,
          tdClass: 'text-end',
          formatter: (value) => convertPrice(value),
        },
      ];
    },

    headerStyle() {
      return {
        fontWeight: 600,
        color: '#181c32',
        backgroundColor: '#f8f9fa',
      };
    },
  },

  methods: {
    convertPrice,

    /**
     * Load danh sách quà tặng đã cấu hình (Bộ phụ kiện tặng kèm) cho SP chính.
     * User chỉ re-attach quà trong danh sách này (không tìm tự do). Quà đã có
     * trong hóa đơn (attachedGiftProductIds) → alreadyAttached + disable.
     * Shape giữ giống fetchProductAttach để addGift → listBillItem nhất quán.
     */
    loadGiftOptions() {
      this.giftOptions = [];
      this.selectAll = false;
      if (!this.productId) return;
      const attached = this.attachedGiftProductIds || [];
      this.isLoading = true;
      ApiService.post('accessories/resolve-attached-gifts', {
        productIds: [this.productId],
        storeId: this.storeId || undefined,
      })
        .then(({ data }) => {
          const map = (data && data.data) || {};
          const items = map[this.productId] || [];
          this.giftOptions = items.map((element) => ({
            id: uuidv4(),
            productId: element.productId,
            name: element.productName,
            productNameVat: element.productNameVat,
            code: `Quà tặng kèm- ${element.accessoryGroupCode}`,
            price: element.sellingPrice,
            quantity: 1,
            type: element.productType,
            imeiCode: '',
            totalPrice: element.sellingPrice,
            discount: '',
            quantityInStock: element.quantityInStock,
            storeId: this.storeId,
            billItemType: BILL_ITEM_TYPE.PRODUCT_BONUS,
            checked: true,
            belongBillDetailId: this.billItemId,
            returnPrice: element.price,
            accessoryGroupId: element.accessoryGroupId,
            accessoryGroupCode: element.accessoryGroupCode,
            listProductVariant: element.listProductVariant,
            isGetVat: false,
            // UI-only — không emit ra listBillItem.
            alreadyAttached: attached.includes(element.productId),
            selected: false,
          }));
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },

    onToggleSelectAll(val) {
      this.giftOptions.forEach((g) => {
        // Quà đã gắn → không cho chọn lại.
        if (g.alreadyAttached) return;
        g.selected = val;
      });
    },

    showModal() {
      this.resetModal();
      this.generateModalTitle();
      this.loadGiftOptions();
      this.$refs.billGiftModal.show();
    },

    hideModal() {
      this.$refs.billGiftModal.hide();
    },

    onModalHidden() {
      this.resetModal();
    },

    resetModal() {
      this.giftOptions = [];
      this.isLoading = false;
      this.selectAll = false;
    },

    generateModalTitle() {
      this.modalTitle = `
		  <div>
			<span class='d-block' style='font-size: 1.1rem'>
			  Thêm quà tặng cho sản phẩm:
			  <span class='text-primary'>#${this.productName}</span>
			</span>
		  </div>
		`;
    },

    applyGifts() {
      const chosen = this.giftOptions.filter((g) => g.selected);
      if (chosen.length === 0) {
        makeToastFaile('Vui lòng chọn ít nhất một sản phẩm quà tặng!');
        return;
      }

      // Bỏ field UI-only (alreadyAttached/selected) trước khi emit.
      const giftList = chosen.map((g) => {
        const gift = { ...g };
        delete gift.alreadyAttached;
        delete gift.selected;
        return gift;
      });

      this.$emit('addGift', giftList);
      this.hideModal();
      makeToastSuccess(`Đã thêm ${giftList.length} sản phẩm quà tặng!`);
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}

.autosuggest__results {
  position: absolute;
  width: 100%;

  max-height: 300px;
  z-index: 1050;
  border: 1px solid #e0e0e0;
  border-radius: 0.375rem;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.autosuggest__results-container {
  position: relative;
  width: 100%;
}

.autosuggest__input {
  display: block;
  width: 100%;
  height: calc(1.35em + 1.1rem + 2px);
  line-height: 1.35;
  padding: 0.55rem 0.75rem;
  font-weight: 400;
  font-size: 0.925rem;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
}

.autosuggest__input:focus {
  outline: none !important;
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.autosuggest__results-item:hover {
  background-color: #f8f9fa;
}

.autosuggest__results ul li {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
}

.autosuggest__results ul li:last-child {
  border-bottom: none;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background-color: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background-color: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background-color: #a8a8a8;
}

.column-gap {
  column-gap: 10px;
}
</style>
